import { Vote } from '@/app/types/proposals';
import Link from 'next/link';
import { formatDate } from '@/app/helpers/formatDate';
import { useState } from 'react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import { truncateAddress64 } from '@/app/helpers/proposalLogHelpers';

interface VotesTableProps {
  votes: Vote[];
}

interface ExpandableTextProps {
  text: string;
  maxLength?: number;
}

const ExpandableText = ({ text, maxLength = 40 }: ExpandableTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return <span className="text-neutral-400">-</span>;
  if (text.length <= maxLength) return <span>{text}</span>;

  return (
    <div className="relative">
      <div className="flex items-start gap-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="inline-flex items-center text-yellow hover:text-white transition-colors shrink-0"
        >
          {isExpanded ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </button>
        <div className="truncate">{!isExpanded && `${text.slice(0, maxLength)}...`}</div>
      </div>
      {isExpanded && <div className="mt-2">{text}</div>}
    </div>
  );
};

const VotesTable = ({ votes }: VotesTableProps) => {
  if (votes.length === 0) return <></>;

  return (
    <div className="mt-8 overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-left border-b border-neutral-700">
            <th className="py-4 px-6 text-yellow">Proposal</th>
            <th className="py-4 px-6 text-yellow">Vote</th>
            <th className="py-4 px-6 text-yellow">Reason</th>
            <th className="py-4 px-6 text-yellow">Creator</th>
            <th className="py-4 px-6 text-yellow text-right">Date</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-700">
          {votes.length > 0 ? (
            votes.map((vote, index) => (
              <tr key={index} className="hover:bg-neutral-900 group">
                <td className="py-4 px-6">
                  <Link
                    href={`/karratco/view/proposal/${vote.proposalId}`}
                    className="hover:text-white transition-colors"
                  >
                    {truncateAddress64(vote.proposalId)}
                  </Link>
                </td>
                <td className="py-4 px-6">
                  <span
                    className={`${
                      vote.vote === 'yes'
                        ? 'text-accent'
                        : vote.vote === 'no'
                          ? 'text-defeated'
                          : 'text-neutral-400'
                    }`}
                  >
                    {vote.vote.toUpperCase()}
                  </span>
                </td>
                <td className="py-4 px-6 max-w-md relative">
                  <ExpandableText text={vote.reason || ''} />
                </td>
                <td className="py-4 px-6 max-w-md relative">
                  {vote.proposer === vote.address ? (
                    <CheckIcon className="h-4 w-4 text-accent" />
                  ) : (
                    <Link
                      href={`/karratco/view/profile/${vote.proposer}`}
                      className="text-neutral-400 hover:text-white transition-colors"
                    >
                      {truncateAddress64(vote.proposer)}
                    </Link>
                  )}
                </td>
                <td className="py-4 px-6 text-right">{formatDate(vote.date)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="py-8 text-center text-neutral-400">
                No voting history found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default VotesTable;
